import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';

/**
 * Initialize once custom events for history API
 */
let isHistoryEventsInited = false;
const initEvents = () => {
  if (isHistoryEventsInited) return;

  isHistoryEventsInited = true;

  const history = window.history;
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function (...args) {
    originalPushState.apply(this, args);
    window.dispatchEvent(new Event('custom-url-change'));
  };

  history.replaceState = function (...args) {
    originalReplaceState.apply(this, args);
    window.dispatchEvent(new Event('custom-url-change'));
  };
};

if (typeof window === 'object') {
  initEvents();
}

/**
 * Get current location state
 */
function getLocationState() {
  if (typeof window === 'undefined') {
    return { pathname: null, search: null, hash: null };
  }
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  };
}

/**
 * Hook for working with browser location
 * @returns
 */
function useBrowserLocation() {
  const [locationState, setLocationState] = useState(getLocationState);

  useEffect(() => {
    const handler = () => {
      setTimeout(() => {
        setLocationState(getLocationState());
      });
    };
    window.addEventListener('custom-url-change', handler);
    return () => window.removeEventListener('custom-url-change', handler);
  }, []);

  const push = useCallback((url: string) => {
    window.history.pushState({}, '', url);
    setLocationState(getLocationState());
  }, []);

  const replace = useCallback((url: string) => {
    window.history.replaceState({}, '', url);
    setLocationState(getLocationState());
  }, []);

  const back = useCallback(() => {
    window.history.back();
  }, []);

  return { ...locationState, push, back, replace };
}

/**
 * Hook proxy useRouter hook for working with browser location
 * @returns
 */
function useHistoryApiRouter() {
  const { pathname, search, hash, push, back, replace } = useBrowserLocation();
  const router = useRouter();

  return {
    push: (url: string) => {
      if (url.startsWith('/space')) {
        return push(url);
      } else {
        router.push(url);
      }
    },
    back,
    replace: (url: string) => {
      if (url.startsWith('/space')) {
        return replace(url);
      } else {
        router.replace(url);
      }
    },
    pathname,
    search,
    hash,
  };
}

/**
 * Hook proxy usePathname hook for working with browser location
 * @returns
 */
function useHistoryApiPathname() {
  const { pathname } = useHistoryApiRouter();
  return pathname;
}

/**
 * Hook proxy useSearchParams hook for working with browser location
 * @returns
 */
function useHistoryApiSearchParams() {
  const { search } = useHistoryApiRouter();
  return useMemo(() => new URLSearchParams(search || ''), [search]);
}

/**
 * Hook proxy useParams hook for working with browser location
 * @returns
 */
function useHistoryApiParams() {
  const { pathname } = useHistoryApiRouter();

  return useMemo(() => {
    const segments = pathname?.replace(/^\/+/, '').split('/');
    if (segments && segments[0] === 'space') {
      return { slug: segments.slice(1) };
    }
    return {};
  }, [pathname]);
}

export {
  useHistoryApiRouter as useRouter,
  useHistoryApiPathname as usePathname,
  useHistoryApiParams as useParams,
  useHistoryApiSearchParams as useSearchParams,
};
